import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateProductDocument,
  type ActivateProductMutation,
  type ActivateProductMutationVariables,
  DeactivateProductDocument,
  type DeactivateProductMutation,
  type DeactivateProductMutationVariables,
  GetProductDocument,
  GetProductsDocument,
} from "~/features/admin/products/gqls";
import { translateError } from "~/lib/errors";

interface ProductActivationTarget {
  id: string;
}

/**
 * Activate / deactivate a product. Returns plain submitters and a `processing`
 * flag; the calling detail view owns the confirmation dialog (V1 parity).
 */
export function useProductActivation() {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [activateProduct] = useMutation<ActivateProductMutation, ActivateProductMutationVariables>(
    ActivateProductDocument,
  );
  const [deactivateProduct] = useMutation<DeactivateProductMutation, DeactivateProductMutationVariables>(
    DeactivateProductDocument,
  );

  const onActivate = useCallback(
    async ({ id }: ProductActivationTarget) => {
      setProcessing(true);
      try {
        await activateProduct({
          variables: { id },
          refetchQueries: [GetProductsDocument, { query: GetProductDocument, variables: { id } }],
        });
        toast.success(t("feature.common.activatedSuccessfully", { model: "product" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activateProduct, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: ProductActivationTarget) => {
      setProcessing(true);
      try {
        await deactivateProduct({
          variables: { id },
          refetchQueries: [GetProductsDocument, { query: GetProductDocument, variables: { id } }],
        });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "product" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivateProduct, t],
  );

  return { onActivate, onDeactivate, processing };
}
